/**
 * Simulation browser component - Terminal aesthetic
 */

import { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { getSimulations } from '../services/api';
import type { Simulation } from '../types/simulation';
import { SimulationCreator } from './SimulationCreator';
import { useNotification } from '../contexts/NotificationContext';
import './SimulationBrowser.css';

interface SimulationBrowserProps {
  onSelectSimulation: (simulation: Simulation) => void;
  onNavigateToDashboard: () => void;
  setTitle: (title: string) => void;
}

export function SimulationBrowser({ onSelectSimulation, onNavigateToDashboard, setTitle }: SimulationBrowserProps) {
  const { showNotification } = useNotification();

  useEffect(() => {
    setTitle('SIMULATION BROWSER');
  }, [setTitle]);

  const [simulations, setSimulations] = useState<Simulation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreator, setShowCreator] = useState(false);

  const fetchSimulations = async () => {
    setIsLoading(true);
    try {
      const data = await getSimulations();
      setSimulations(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load simulations');
      console.error('Failed to fetch simulations:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSimulations();
  }, []);

  const handleSimulationCreated = (simulation: Simulation) => {
    setShowCreator(false);
    // Add the new simulation to the list immediately
    setSimulations(prev => [...prev, simulation]);
  };

  // Create portal content for toolbar
  const portalTarget = document.getElementById('global-toolbar-portal');
  const toolbarContent = portalTarget ? ReactDOM.createPortal(
    <>
      <div className="toolbar-left">
        <div className="toolbar-link" onClick={onNavigateToDashboard}>
          dashboard
        </div>
      </div>
      <div className="toolbar-right">
        <div className="toolbar-link" onClick={() => setShowCreator(true)}>
          + new simulation
        </div>
      </div>
    </>,
    portalTarget
  ) : null;

  const handleCopyLink = (e: React.MouseEvent, simId: string) => {
    e.stopPropagation();
    const url = new URL(window.location.origin);
    url.searchParams.set('sim', simId);
    navigator.clipboard.writeText(url.toString());
    showNotification('Link copied to clipboard!');
  };

  const handleCopySimId = (e: React.MouseEvent, simId: string) => {
    e.stopPropagation();
    navigator.clipboard.writeText(simId);
    showNotification('Simulation ID copied!');
  };

  return (
    <div className="simulation-browser">
      {toolbarContent}

      {showCreator && (
        <SimulationCreator
          onSimulationCreated={handleSimulationCreated}
          onCancel={() => setShowCreator(false)}
        />
      )}

      {isLoading && (
        <div className="browser-loading">
          <div className="loading-spinner"></div>
          <p>&gt; LOADING SIMULATIONS...</p>
        </div>
      )}

      {error && (
        <div className="browser-error">
          <p>ERROR: {error}</p>
          <button onClick={() => fetchSimulations()}>RETRY</button>
        </div>
      )}

      {!showCreator && !isLoading && !error && (
        <>

      {simulations.length === 0 ? (
        <div className="browser-empty">
          <p>&gt; NO SIMULATIONS AVAILABLE</p>
          <p className="empty-hint">Create your first simulation to get started</p>
        </div>
      ) : (
        <div className="simulation-grid">
          {simulations.map((sim) => {
            const aiModels = sim.ai_models || [];
            return (
              <button
                key={sim.id}
                className={`simulation-card simulation-card--${sim.status}`}
                onClick={() => onSelectSimulation(sim)}
                disabled={sim.status !== 'active'}
              >
                <button
                  className="card-share-btn"
                  onClick={(e) => handleCopySimId(e, sim.id)}
                  title="Copy simulation ID"
                  aria-label="Copy simulation ID"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                  </svg>
                </button>
                <button
                  className="card-share-btn"
                  onClick={(e) => handleCopyLink(e, sim.id)}
                  title="Copy sharing link"
                  aria-label="Copy sharing link"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"></path>
                    <polyline points="16 6 12 2 8 6"></polyline>
                    <line x1="12" y1="2" x2="12" y2="15"></line>
                  </svg>
                </button>
                <div className="card-content">
                  <h3 className="card-title">{sim.name}</h3>
                  {sim.description && <p className="card-description">{sim.description}</p>}
                  <div className="card-meta">
                    <span className={`status-badge status-badge--${sim.status}`}>
                      {sim.status.toUpperCase()}
                    </span>
                    {aiModels.length > 0 && (
                      <span className="ai-count">
                        {aiModels.length} AI Model{aiModels.length !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                  {aiModels.length > 0 && (
                    <div className="card-ai-list">
                      {aiModels.map((ai, idx) => (
                        <span key={idx} className="ai-badge">
                          {ai.ai_name}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}
        </>
      )}
    </div>
  );
}
